import { useCallback, useState } from 'react';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalFooter,
  Box,
  Text,
  VStack,
  Icon,
  Button,
  useToast,
  Progress,
  Input,
  Stack,
  HStack,
  Tooltip,
  Divider,
  Badge,
} from '@chakra-ui/react';
import { FiUpload, FiFile, FiCheck, FiInfo } from 'react-icons/fi';
import { gql, useMutation, useApolloClient } from '@apollo/client';
import { HelivanText } from '../helivan/HelivanText';
import { HelivanButton } from '../helivan/HelivanButton';
import { getAuth } from 'firebase/auth';
import { useCreateEvaluationMutation } from '../../generated/graphql';
import {
  parseEvaluationJSON,
  EvaluationStatistics,
  EXPECTED_JSON_FORMAT_EXAMPLE,
} from '../../utils/evaluationParser';

interface EvaluationsCreateNewModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const EvaluationsCreateNewModal = ({
  isOpen,
  onClose,
}: EvaluationsCreateNewModalProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [step, setStep] = useState<1 | 2>(1); // 1 = upload, 2 = preview/confirm
  const [statistics, setStatistics] = useState<EvaluationStatistics | null>(
    null
  );
  const [filteredJson, setFilteredJson] = useState<any>(null);
  const [evaluationName, setEvaluationName] = useState('');
  const toast = useToast();
  const client = useApolloClient();
  const [createEvaluation] = useCreateEvaluationMutation();

  const validateFile = (file: File): boolean => {
    if (!file.name.endsWith('.json')) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload a JSON file (.json)',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
    return true;
  };

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const droppedFiles = e.dataTransfer.files;
      if (droppedFiles.length > 0) {
        const droppedFile = droppedFiles[0];
        if (validateFile(droppedFile)) {
          setFile(droppedFile);
        }
      }
    },
    [toast]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      const selectedFile = selectedFiles[0];
      if (validateFile(selectedFile)) {
        setFile(selectedFile);
      }
    }
  };

  const handleParseAndPreview = async () => {
    console.log('🚀 [EVALUATION] handleParseAndPreview started', {
      hasFile: !!file,
      evaluationName,
      fileName: file?.name,
      fileSize: file?.size,
    });

    if (!file || !evaluationName) {
      console.error('❌ [EVALUATION] Missing required fields', {
        hasFile: !!file,
        hasEvaluationName: !!evaluationName,
      });
      toast({
        title: 'Missing Information',
        description: 'Please provide both evaluation name and file',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsUploading(true);
    console.log('📝 [EVALUATION] Starting parse process...');

    try {
      // Read file content to validate JSON
      console.log('📖 [EVALUATION] Reading file content...');
      const fileContent = await file.text();
      console.log('✅ [EVALUATION] File read successfully', {
        contentLength: fileContent.length,
      });

      // Validate and parse JSON
      console.log('🔍 [EVALUATION] Validating and parsing JSON...');

      // Check for BOM and remove it if present
      let cleanContent = fileContent;
      if (fileContent.charCodeAt(0) === 0xfeff) {
        console.log('⚠️ [EVALUATION] BOM detected, removing...');
        cleanContent = fileContent.slice(1);
      }

      // Replace invalid JSON values (NaN, Infinity, -Infinity) with null
      // These are valid JavaScript but not valid JSON
      const nanInfinityCount = (
        cleanContent.match(/:\s*(NaN|Infinity|-Infinity)/g) || []
      ).length;
      if (nanInfinityCount > 0) {
        console.log(
          `⚠️ [EVALUATION] Found ${nanInfinityCount} NaN/Infinity values, replacing with null...`
        );
        cleanContent = cleanContent
          .replace(/:\s*NaN\s*([,}\]])/g, ': null$1')
          .replace(/:\s*Infinity\s*([,}\]])/g, ': null$1')
          .replace(/:\s*-Infinity\s*([,}\]])/g, ': null$1');
      }

      // Log the first 200 characters for debugging
      console.log(
        '📝 [EVALUATION] First 200 chars:',
        cleanContent.substring(0, 200)
      );
      console.log(
        '📝 [EVALUATION] Last 200 chars:',
        cleanContent.substring(cleanContent.length - 200)
      );

      let jsonData;
      try {
        jsonData = JSON.parse(cleanContent);
        console.log('✅ [EVALUATION] JSON is valid', {
          keys: Object.keys(jsonData).slice(0, 5),
          totalKeys: Object.keys(jsonData).length,
        });
      } catch (err: any) {
        console.error('❌ [EVALUATION] Invalid JSON:', err);
        console.error('Error position:', err.message);

        // Try to extract the position from the error message
        const positionMatch = err.message.match(/position (\d+)/);
        const position = positionMatch ? parseInt(positionMatch[1]) : null;

        let detailedError = 'The file does not contain valid JSON';
        if (position !== null) {
          const start = Math.max(0, position - 50);
          const end = Math.min(cleanContent.length, position + 50);
          const context = cleanContent.substring(start, end);
          detailedError = `JSON parse error at position ${position}. Context: "${context}"`;
          console.error('Context around error:', context);
        }

        toast({
          title: 'Invalid JSON',
          description: detailedError,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        setIsUploading(false);
        return;
      }

      // Parse the evaluation JSON and get statistics
      console.log('📊 [EVALUATION] Parsing evaluation structure...');
      const parseResult = parseEvaluationJSON(jsonData);
      console.log('✅ [EVALUATION] Parse complete:', parseResult.statistics);

      // Store both the statistics and the filtered JSON for upload
      setStatistics(parseResult.statistics);
      setFilteredJson(parseResult.filteredJson);
      setIsUploading(false);
      setStep(2);

      toast({
        title: 'File validated successfully',
        description: 'Please review the evaluation details',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error('❌ [EVALUATION] Validation failed:', {
        error: error.message,
        stack: error.stack,
      });

      // Show detailed error message
      const errorLines = error.message.split('\n');
      const errorDescription =
        errorLines.length > 1
          ? errorLines.slice(0, 5).join('\n') +
            (errorLines.length > 5 ? '\n...' : '')
          : error.message;

      toast({
        title: 'Invalid JSON Format',
        description: errorDescription,
        status: 'error',
        duration: 10000,
        isClosable: true,
      });
      setIsUploading(false);
    }
  };

  const handleConfirmAndUpload = async () => {
    console.log('🚀 [EVALUATION] handleConfirmAndUpload started');

    if (!file || !evaluationName || !filteredJson) {
      console.error('❌ [EVALUATION] Missing required data');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    console.log('📝 [EVALUATION] Starting upload process...');

    try {
      setUploadProgress(10);
      console.log('📤 [EVALUATION] Preparing to upload to Firebase Storage...');
      toast({
        title: 'Uploading file',
        description: 'Uploading your evaluation file to storage...',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });

      // Step 1: Upload filtered/parsed file to Firebase Storage
      console.log('🔑 [EVALUATION] Getting auth token...');
      const auth = getAuth();
      const user = auth.currentUser;
      let token = '';
      try {
        if (user) {
          token = await user.getIdToken();
          console.log('✅ [EVALUATION] Auth token obtained');
        } else {
          console.warn('⚠️ [EVALUATION] No user found');
        }
      } catch (error) {
        console.error('❌ [EVALUATION] Error getting auth token:', error);
      }

      // Create a new File from the filtered JSON
      console.log('📝 [EVALUATION] Creating file from filtered JSON...');
      const filteredJsonString = JSON.stringify(filteredJson, null, 2);
      const filteredBlob = new Blob([filteredJsonString], {
        type: 'application/json',
      });
      const filteredFile = new File([filteredBlob], file.name, {
        type: 'application/json',
      });
      console.log('✅ [EVALUATION] Filtered file created', {
        originalSize: file.size,
        filteredSize: filteredFile.size,
      });

      const formData = new FormData();
      formData.append('file', filteredFile);
      console.log('📦 [EVALUATION] FormData created with filtered JSON');

      // Get the base URL from Apollo client
      const baseUrl =
        (client.link as any)?.options?.uri || 'http://localhost:4000/graphql';
      const apiBaseUrl = baseUrl.replace(/\/graphql$/, '');
      const uploadUrl = `${apiBaseUrl}/api/evaluation-files/upload`;
      console.log('🌐 [EVALUATION] Upload URL:', uploadUrl);

      setUploadProgress(30);

      console.log('📡 [EVALUATION] Sending upload request...');
      const uploadResponse = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: formData,
      });

      console.log('📥 [EVALUATION] Upload response received', {
        status: uploadResponse.status,
        ok: uploadResponse.ok,
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.error('❌ [EVALUATION] Upload failed:', {
          status: uploadResponse.status,
          errorText,
        });
        throw new Error('Failed to upload file to storage');
      }

      const uploadData = await uploadResponse.json();
      const fileUrl = uploadData.fileUrl;
      console.log('✅ [EVALUATION] File uploaded to Firebase!', {
        fileUrl: fileUrl.substring(0, 100) + '...',
        fileId: uploadData.fileId,
      });

      setUploadProgress(60);
      toast({
        title: 'Creating evaluation',
        description: 'Processing your evaluation...',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });

      // Step 2: Create evaluation via GraphQL mutation with fileUrl
      console.log('🔄 [EVALUATION] Calling GraphQL mutation...', {
        evaluationName,
        fileUrlPreview: fileUrl.substring(0, 100) + '...',
      });

      const result = await createEvaluation({
        variables: {
          evaluationName,
          fileUrl,
        },
      });

      console.log('📩 [EVALUATION] GraphQL response received:', {
        success: result.data?.createEvaluation?.success,
        evaluationId: result.data?.createEvaluation?.evaluationId,
        errorMessage: result.data?.createEvaluation?.errorMessage,
        errorCode: result.data?.createEvaluation?.errorCode,
      });

      setUploadProgress(100);

      if (result.data?.createEvaluation?.success) {
        console.log('🎉 [EVALUATION] Evaluation created successfully!');
        setIsUploading(false);
        toast({
          title: 'Success',
          description: `Evaluation "${evaluationName}" created successfully!`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        onClose();
        setFile(null);
        setEvaluationName('');
        setStatistics(null);
        setFilteredJson(null);
        setUploadProgress(0);
      } else {
        const errorMsg =
          result.data?.createEvaluation?.errorMessage ||
          'Failed to create evaluation';
        console.error('❌ [EVALUATION] Evaluation creation failed:', errorMsg);
        throw new Error(errorMsg);
      }
    } catch (error: any) {
      console.error('❌ [EVALUATION] Upload process failed:', {
        error: error.message,
        stack: error.stack,
      });
      toast({
        title: 'Upload failed',
        description:
          error.message || 'Failed to upload the file. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
  };

  const handleBack = () => {
    setStep(1);
    setStatistics(null);
    setFilteredJson(null);
  };

  const handleClose = () => {
    setFile(null);
    setEvaluationName('');
    setStep(1);
    setStatistics(null);
    setFilteredJson(null);
    setUploadProgress(0);
    setIsUploading(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <ModalOverlay />
      <ModalContent maxH="80vh" display="flex" flexDirection="column">
        <ModalHeader>
          {step === 1 ? 'Create New Evaluation' : 'Confirm Evaluation Details'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6} overflowY="auto" flex="1">
          {step === 1 ? (
            <VStack spacing={4} align="stretch">
              <Stack spacing={1}>
                <HStack spacing={2} align="center">
                  <HelivanText variant="largeSubheader">
                    Evaluation Name
                  </HelivanText>
                </HStack>
                <Input
                  bg="white"
                  px={5}
                  py={4}
                  width="full"
                  borderRadius="12px"
                  height="50px"
                  borderWidth="1px"
                  value={evaluationName}
                  onChange={(e) => setEvaluationName(e.target.value)}
                  placeholder={'Evaluation Name'}
                />
              </Stack>
              <Stack spacing={1}>
                <HStack spacing={2} align="center">
                  <HelivanText variant="largeSubheader">
                    Upload JSON File
                  </HelivanText>
                  <Tooltip
                    label={
                      <Box maxW="400px" p={2}>
                        <Text fontWeight="bold" mb={2}>
                          Expected JSON Format:
                        </Text>
                        <Box
                          as="pre"
                          fontSize="xs"
                          bg="gray.800"
                          p={2}
                          borderRadius="md"
                          overflow="auto"
                        >
                          {JSON.stringify(
                            EXPECTED_JSON_FORMAT_EXAMPLE,
                            null,
                            2
                          )}
                        </Box>
                      </Box>
                    }
                    hasArrow
                    placement="right"
                  >
                    <Box>
                      <Icon as={FiInfo} color="gray.500" cursor="help" />
                    </Box>
                  </Tooltip>
                </HStack>
                {!file ? (
                  <Box
                    border="2px dashed"
                    borderColor={isDragging ? 'blue.400' : 'gray.300'}
                    borderRadius="lg"
                    p={8}
                    textAlign="center"
                    bg={isDragging ? 'blue.50' : 'gray.50'}
                    cursor="pointer"
                    transition="all 0.2s"
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={() =>
                      document.getElementById('file-input')?.click()
                    }
                    _hover={{ borderColor: 'blue.400', bg: 'blue.50' }}
                  >
                    <VStack spacing={3}>
                      <Icon as={FiUpload} boxSize={12} color="gray.400" />
                      <Text fontSize="lg" fontWeight="medium">
                        Drop your JSON file here
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        or click to browse
                      </Text>
                      <Text fontSize="xs" color="gray.400">
                        Supports files up to 1 billion characters
                      </Text>
                    </VStack>
                    <input
                      id="file-input"
                      type="file"
                      accept=".json"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                  </Box>
                ) : (
                  <Box
                    border="2px solid"
                    borderColor="green.400"
                    borderRadius="lg"
                    p={6}
                    bg="green.50"
                  >
                    <VStack spacing={3} align="stretch">
                      <Box display="flex" alignItems="center" gap={3}>
                        <Icon as={FiCheck} color="green.500" boxSize={6} />
                        <Box flex={1}>
                          <Text fontWeight="medium" fontSize="md">
                            {file.name}
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            {(file.size / 1024).toFixed(2)} KB
                          </Text>
                        </Box>
                      </Box>
                      <Box display="flex" gap={2}>
                        <Button
                          size="sm"
                          onClick={handleRemoveFile}
                          isDisabled={isUploading}
                        >
                          Remove
                        </Button>
                      </Box>
                    </VStack>
                  </Box>
                )}
              </Stack>
              <HelivanButton
                alignSelf="flex-end"
                width="fit-content"
                variant="primary"
                onClick={handleParseAndPreview}
                isDisabled={isUploading || !evaluationName || !file}
                isLoading={isUploading}
              >
                {isUploading ? 'Validating...' : 'Next'}
              </HelivanButton>
            </VStack>
          ) : (
            <VStack spacing={4} align="stretch">
              <Box>
                <HelivanText variant="largeSubheader" mb={3}>
                  Evaluation Summary
                </HelivanText>
                <VStack spacing={3} align="stretch">
                  <HStack justify="space-between">
                    <Text color="gray.600">Name:</Text>
                    <Text fontWeight="bold">{evaluationName}</Text>
                  </HStack>
                  <Divider />
                  <HStack justify="space-between">
                    <Text color="gray.600">Number of Models:</Text>
                    <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                      {statistics?.num_models}
                    </Badge>
                  </HStack>
                  <HStack justify="space-between">
                    <Text color="gray.600">Number of Subtasks:</Text>
                    <Badge colorScheme="purple" fontSize="md" px={3} py={1}>
                      {statistics?.num_subtasks}
                    </Badge>
                  </HStack>
                  <HStack justify="space-between">
                    <Text color="gray.600">Total Queries:</Text>
                    <Badge colorScheme="green" fontSize="md" px={3} py={1}>
                      {statistics?.total_queries}
                    </Badge>
                  </HStack>
                  <Divider />
                  <Box>
                    <Text color="gray.600" mb={2}>
                      Queries per Subtask:
                    </Text>
                    <VStack spacing={2} align="stretch" pl={4}>
                      {statistics &&
                        Object.entries(statistics.queries_per_subtask).map(
                          ([subtask, count]) => (
                            <HStack key={subtask} justify="space-between">
                              <Text fontSize="sm">{subtask}:</Text>
                              <Badge colorScheme="gray">{count}</Badge>
                            </HStack>
                          )
                        )}
                    </VStack>
                  </Box>
                  <Divider />
                  <Box>
                    <Text color="gray.600" mb={2}>
                      Models ({statistics?.num_models}):
                    </Text>
                    <Box
                      maxH="150px"
                      overflowY="auto"
                      bg="gray.50"
                      p={3}
                      borderRadius="md"
                    >
                      <VStack spacing={1} align="stretch">
                        {statistics?.models.map((model) => (
                          <Text key={model} fontSize="sm">
                            • {model}
                          </Text>
                        ))}
                      </VStack>
                    </Box>
                  </Box>
                </VStack>
              </Box>
            </VStack>
          )}
        </ModalBody>
        {step === 2 && (
          <ModalFooter
            borderTop="1px solid"
            borderColor="gray.200"
            flexDirection="column"
            gap={3}
          >
            {isUploading && (
              <Box width="full">
                <Progress
                  value={uploadProgress}
                  size="sm"
                  colorScheme="blue"
                  hasStripe
                  isAnimated
                />
                <Text
                  fontSize="xs"
                  color="gray.500"
                  mt={1}
                  textAlign="center"
                >
                  {uploadProgress < 30
                    ? 'Preparing file...'
                    : uploadProgress < 60
                      ? 'Uploading to storage...'
                      : uploadProgress < 100
                        ? 'Creating evaluation... This may take a minute, do not close or refresh the window.'
                        : 'Complete!'}
                </Text>
              </Box>
            )}
            <HStack justify="space-between" width="full">
              <HelivanButton
                variant="outline"
                onClick={handleBack}
                isDisabled={isUploading}
              >
                Back
              </HelivanButton>
              <HelivanButton
                variant="primary"
                onClick={handleConfirmAndUpload}
                isDisabled={isUploading}
                isLoading={isUploading}
              >
                {isUploading ? 'Creating...' : 'Confirm & Create'}
              </HelivanButton>
            </HStack>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};
